import { Box, lighten, ListItemButton, ListItemIcon, Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'
import { MouseEvent, ReactNode, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'

import { EntityLocate, SearchResultRowProps } from '#/components/AdvancedSearch/types'
import { Dialog } from '#/components/Dialog'
import FindMyCow, { FindMyCowButton } from '#/components/FindMyCow'
import LineClamp from '#/styles/LineClamp'

export function SearchResultRow({
  action,
  crumbLabel,
  description,
  icon,
  isActive,
  label,
  link,
  onClick,
  onMouseMove,
  searchTerm,
}: SearchResultRowProps) {
  const itemRef = useRef<HTMLAnchorElement>(null)

  // Item ativado pelo mouse já está sob o cursor; rolar o desloca e dispara novo mousemove
  useEffect(() => {
    if (isActive && !itemRef.current?.matches(':hover')) {
      itemRef.current?.scrollIntoView({ block: 'nearest' })
    }
  }, [isActive])

  return (
    <ListItemButton
      ref={itemRef}
      to={link}
      onClick={onClick}
      onMouseMove={onMouseMove}
      sx={{
        '&:hover': {
          backgroundColor: 'transparent',
        },
        'borderRadius': 2,
        'my': 0.75,
        'py': 0.5,
        ...(action ? { justifyContent: 'space-between' } : {}),
        ...(isActive ? searchItemHoverSx : {}),
      }}
      component={Link}
    >
      <Box sx={{ alignItems: 'center', display: 'flex', minWidth: 0 }}>
        <ListItemIcon sx={{ minWidth: 40, mr: 1 }}>{icon}</ListItemIcon>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography variant="body1">{highlightMatch(label, searchTerm)}</Typography>
          {crumbLabel && (
            <Typography variant="caption" sx={{ color: 'grey.600' }}>
              {highlightMatch(crumbLabel, searchTerm)}
            </Typography>
          )}
          {description && (
            <Typography variant="body2" sx={{ color: 'grey.600', ...LineClamp, maxWidth: 'unset' }}>
              {highlightMatch(description, searchTerm)}
            </Typography>
          )}
        </Box>
      </Box>
      {action}
    </ListItemButton>
  )
}

export function EntityLocateButton({
  farmSlug,
  locate,
  onLocate,
}: {
  farmSlug?: string
  locate: EntityLocate
  onLocate: () => void
}) {
  const handleClick = (event: MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    Dialog.open({
      content: (
        <FindMyCow
          farm={farmSlug}
          animal={locate.type === 'animal' ? locate.identifier : undefined}
          collar={locate.type === 'collar' ? locate.identifier : undefined}
          type={locate.type}
        />
      ),
      maxWidth: 'md',
    })
    onLocate()
  }

  return <FindMyCowButton onClick={handleClick} />
}

const searchItemHoverSx = {
  '&:hover': {
    backgroundColor: (theme: Theme) => lighten(theme.palette.primary.light, 0.85),
  },
  'backgroundColor': (theme: Theme) => lighten(theme.palette.primary.light, 0.9),
  'color': 'primary.main',
}

/** Envolve em strong os trechos de text que casam com term, ignorando maiúsculas */
function highlightMatch(text: string, term?: string): ReactNode {
  if (!term) return text

  const normalizedText = String(text)
  const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const parts = normalizedText.split(new RegExp(`(${escapedTerm})`, 'gi'))
  if (parts.length === 1) return normalizedText

  return parts.map((part, index) =>
    part.toLowerCase() === term.toLowerCase() ? <strong key={index}>{part}</strong> : part,
  )
}
